import type {
    DesignSet,
    PartsProduct,
    ArrowType,
    OrderType,
} from '@app/react/const/type';
import {
    KINTEKI,
    ENTEKI,
    MAKIWARA,
    FULL,
    SEMI,
} from '@app/react/const/type';
import { isFeatherNone } from '@app/react/common/isFeatherNone';
import { isSelectedAdjustProduct } from '@app/react/common/isSelectedAdjustProduct';

const isNotNull = (value: PartsProduct | boolean | null) => value !== null;

const kintekiFull = (item: DesignSet): boolean => {
    const checkValues = [
        item.shaft,
        item.feather,
        item.thread,
        item.kebiki,
        item.nock,
        item.arrowhead,
        item.unit,
    ];

    const al = isSelectedAdjustProduct(item.arrowlengthAdjust, item.arrowlength) === 'valid';

    return checkValues.every(isNotNull) && al;
};

const kintekiSemi = (item: DesignSet): boolean => {
     const require = [
        item.shaft,
        item.feather,
        item.thread,
        item.kebiki,
        item.nock,
        item.arrowhead,
        item.unit,
     ];

    const al = isSelectedAdjustProduct(item.arrowlengthAdjust, item.arrowlength) === 'valid';

    const invalid = [
        item.twine,
        item.lame,
        item.carving,
        item.familycrest,
        item.decoration,
        item.featherinside,
        item.typezero,
    ];

    return require.every(isNotNull) && al && !invalid.some(isNotNull);
};

const entekiFull = (item: DesignSet): boolean => {
    const require = [
        item.shaft,
        item.feather,
        item.thread,
        item.kebiki,
        item.nock,
        item.arrowhead,
        item.unit,
    ];

    const al = isSelectedAdjustProduct(item.arrowlengthAdjust, item.arrowlength) === 'valid';

    const invalid = [
        item.familycrest,
    ];

    return require.every(isNotNull) && al && !invalid.some(isNotNull);
};

const entekiSemi = (item: DesignSet): boolean => {
    const require = [
        item.shaft,
        item.feather,
        item.thread,
        item.kebiki,
        item.nock,
        item.arrowhead,
        item.unit,
    ];

    const al = isSelectedAdjustProduct(item.arrowlengthAdjust, item.arrowlength) === 'valid';

    const invalid = [
        item.twine,
        item.lame,
        item.carving,
        item.familycrest,
        item.decoration,
        item.featherinside,
        item.typezero,
    ];

    return require.every(isNotNull) && al && !invalid.some(isNotNull);
};

const makiwaraFullWithFeather = (item: DesignSet): boolean => {
    const require = [
        item.shaft,
        item.feather,
        item.thread,
        item.kebiki,
        item.nock,
        item.arrowhead,
        item.unit,
    ];

    const al = isSelectedAdjustProduct(item.arrowlengthAdjust, item.arrowlength) === 'valid';

    const invalid = [
        item.familycrest,
    ];

    return require.every(isNotNull) && al && !invalid.some(isNotNull);
};

const makiwaraFullWithoutFeather = (item: DesignSet): boolean => {
    const require = [
        item.shaft,
        item.feather,
        item.thread,
        item.kebiki,
        item.nock,
        item.arrowhead,
        item.unit,
    ];

    const al = isSelectedAdjustProduct(item.arrowlengthAdjust, item.arrowlength) === 'valid';

    const invalid = [
        item.familycrest,
        item.featherinside,
    ];

    return require.every(isNotNull) && al && !invalid.some(isNotNull);
};

type F = { [at in ArrowType]: { [ ot in OrderType ]: (item: DesignSet) => boolean } };

const functionTable: F = {
    [KINTEKI]: {
        [FULL]: kintekiFull,
        [SEMI]: kintekiSemi,
    },
    [ENTEKI]: {
        [FULL]: entekiFull,
        [SEMI]: entekiSemi,
    },
    [MAKIWARA]: {
        [FULL]: () => false,
        [SEMI]: () => false,
    },
};

export const validateCart = (at: ArrowType, ot: OrderType, item: DesignSet): boolean => {
    if (at === MAKIWARA && ot === FULL) {
        if (isFeatherNone(item.feather)) {
            return makiwaraFullWithoutFeather(item);
        }
        return makiwaraFullWithFeather(item);
    }

    const validator = functionTable[at][ot];
    return validator(item);
};
